/**
 * City Planning Hotels — LiteAPI live search (mirrors iOS HotelPlanningView).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Alert, Spin, message } from 'antd';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscriptionFeature } from '../../contexts/SubscriptionContext';
import { cityPlanningEn } from '../../content/cityPlanning.en';
import { tripLodgingEn } from '../../content/tripLodging.en';
import {
  getStoredPlanData,
  hydrateCityPlanFromFile,
} from '../../services/aiPlanPersistService';
import { getAccessibleTrip, updateTrip } from '../../services/tripsService';
import { useCanonicalTripUrl } from '../../utils/useCanonicalTripUrl';
import type { AggregatedPlanData } from '../../utils/aggregatedPlanData';
import type { Trip } from '../../types/client';
import { effectiveCanEdit } from '../../utils/tripAccessPolicy';
import { defaultLodgingDateRange } from '../../utils/cruiseLodgingCityVisitFilter';
import { upsertHotelStayOnTrip } from '../../utils/hotelStayMutations';
import { CityPlanningChrome } from './cityPlanningShared';
import HotelLiveSearchPanel from '../../components/HotelLiveSearchPanel/HotelLiveSearchPanel';
import styles from './CityPlanning.module.css';

const CityPlanningHotels: React.FC = () => {
  const { tripId } = useParams<{ tripId: string }>();
  const [searchParams] = useSearchParams();
  const cityVisitId = searchParams.get('cityVisitId')?.trim() ?? '';
  const { user } = useAuth();
  const includesAIPlan = useSubscriptionFeature('aiPlan');

  const [loading, setLoading] = useState(true);
  const [plan, setPlan] = useState<AggregatedPlanData | null>(null);
  const [trip, setTrip] = useState<Trip | null>(null);

  const reloadTrip = useCallback((): void => {
    if (!user?.sub || !tripId) {
      setTrip(null);
      return;
    }
    setTrip(getAccessibleTrip(user.sub, tripId));
  }, [user?.sub, tripId]);

  useEffect(() => {
    reloadTrip();
  }, [reloadTrip]);

  useCanonicalTripUrl(trip);

  const cityName =
    (cityVisitId && trip?.cityNames?.[cityVisitId]) || cityPlanningEn.untitledCity;
  const canEdit = Boolean(trip && user?.sub && effectiveCanEdit(trip, user.sub));

  const dateRange = useMemo(() => {
    if (!trip || !cityVisitId) return { checkIn: '', checkOut: '' };
    return defaultLodgingDateRange(trip, cityVisitId);
  }, [trip, cityVisitId]);

  useEffect(() => {
    let cancelled = false;
    const run = async (): Promise<void> => {
      if (!trip?.id || !cityVisitId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      let next = getStoredPlanData(trip, cityVisitId);
      if (!next) {
        next = await hydrateCityPlanFromFile(trip.id, cityVisitId);
      }
      if (!cancelled) {
        setPlan(next);
        setLoading(false);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [trip?.id, cityVisitId]);

  if (!includesAIPlan) {
    return (
      <div className={styles.page}>
        <div className={styles.container}>
          <Alert type="info" message={cityPlanningEn.noSubscription} showIcon />
        </div>
      </div>
    );
  }

  if (!tripId || !cityVisitId) {
    return (
      <div className={styles.page}>
        <div className={styles.container}>
          <Alert type="warning" message={cityPlanningEn.missingCity} showIcon />
        </div>
      </div>
    );
  }

  if (!trip && !loading) {
    return (
      <div className={styles.page}>
        <div className={styles.container}>
          <Alert type="error" message={cityPlanningEn.missingTrip} showIcon />
        </div>
      </div>
    );
  }

  return (
    <CityPlanningChrome
      trip={trip}
      userId={user?.sub}
      cityName={cityName}
      cityVisitId={cityVisitId}
      section="hotels"
      canEdit={canEdit}
    >
      {!canEdit ? (
        <Alert
          type="info"
          message={cityPlanningEn.readOnly}
          showIcon
          className={styles.statusBanner}
        />
      ) : null}

      {loading ? (
        <div className={styles.loading}>
          <Spin />
        </div>
      ) : null}

      {!loading && trip ? (
        <HotelLiveSearchPanel
          trip={trip}
          cityVisitId={cityVisitId}
          cityName={cityName}
          plan={plan}
          checkIn={dateRange.checkIn}
          checkOut={dateRange.checkOut}
          canEdit={canEdit}
          currencyCode={trip.currency}
          onSaved={(stay) => {
            if (!user?.sub || !trip) return;
            const patch = upsertHotelStayOnTrip({
              trip,
              cityVisitId,
              stay,
            });
            const updated = updateTrip(user.sub, trip.id, patch);
            if (updated) setTrip(updated);
            message.success(tripLodgingEn.save);
          }}
        />
      ) : null}
    </CityPlanningChrome>
  );
};

export default CityPlanningHotels;
